import React from 'react';
import {
  LegalPageLayout,
  LegalPageTitle,
  LegalPageUpdated,
  LegalNotice,
  LegalSection,
  LegalHeading,
  LegalBody,
  LegalList,
} from '../../shared/layouts/LegalPageLayout';

export interface PrivacyPolicyPageProps {
  onBack: () => void;
}

export const PrivacyPolicyPage: React.FC<PrivacyPolicyPageProps> = ({ onBack }) => {
  return (
    <LegalPageLayout onBack={onBack}>
      <LegalPageTitle>Privacy Policy</LegalPageTitle>
      <LegalPageUpdated>Last updated: {new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}</LegalPageUpdated>

      <LegalNotice>
        This is a working draft prepared to describe Tvikla&apos;s actual data flows. It has not been
        reviewed by a lawyer and should not be published or relied on as a final policy until qualified
        counsel has reviewed it against applicable law (including India&apos;s Digital Personal Data
        Protection Act, 2023, and any regimes that apply to your buyers, such as GDPR).
      </LegalNotice>

      <LegalSection>
        <LegalHeading>1. Scope</LegalHeading>
        <LegalBody>
          This policy explains what Tvikla OS collects, why, and how it is used when you use the
          platform as an exporter, a buyer, or a team member on either side of a trade. It applies to
          the web application, the marketplace, and any related communications.
        </LegalBody>
      </LegalSection>

      <LegalSection>
        <LegalHeading>2. What we collect</LegalHeading>
        <LegalBody>Depending on how you use Tvikla, we collect:</LegalBody>
        <LegalList>
          <li><strong>Account &amp; business identity data</strong> — name, email, phone, company details, and trade credentials such as IEC, GST number, and AD code, used to verify exporters and file with government systems on your behalf.</li>
          <li><strong>Order &amp; shipment data</strong> — production status, vessel and container bookings, insurance cover, and delivery milestones tied to each order.</li>
          <li><strong>Customs &amp; compliance data</strong> — shipping bill details and filing status exchanged with ICEGATE, and logistics status pulled from ULIP.</li>
          <li><strong>Payment &amp; banking data</strong> — invoice, collection, and reconciliation records processed through our banking and payment partners. Tvikla does not store full card or full bank account numbers; those are handled by our regulated payment partners.</li>
          <li><strong>Messages</strong> — buyer–supplier conversations on the platform, including content needed to provide translation between languages.</li>
          <li><strong>Usage data</strong> — device, browser, and log information used to keep the platform secure and to fix problems.</li>
        </LegalList>
      </LegalSection>

      <LegalSection>
        <LegalHeading>3. How we use this information</LegalHeading>
        <LegalList>
          <li>To operate the marketplace, orders, scheduling, and tracking features you use directly.</li>
          <li>To file and track customs declarations and logistics status with the relevant government and carrier systems on your behalf.</li>
          <li>To process payments, run reconciliation, and support compliance checks required for cross-border trade.</li>
          <li>To translate and deliver messages between buyers and suppliers.</li>
          <li>To secure the platform, prevent fraud, and meet our own legal and regulatory obligations.</li>
        </LegalList>
      </LegalSection>

      <LegalSection>
        <LegalHeading>4. Who we share it with</LegalHeading>
        <LegalBody>
          We share data only with the parties needed to complete your trade: government and regulatory
          systems (such as ICEGATE, ULIP, DGFT, and GST), the carriers you book with, the banks and
          payment processors that move funds, and the buyer or supplier on the other side of a given
          order — limited to what that order requires. We do not sell personal data to advertisers or
          data brokers.
        </LegalBody>
      </LegalSection>

      <LegalSection>
        <LegalHeading>5. Cross-border transfers</LegalHeading>
        <LegalBody>
          Because Tvikla connects Indian exporters with buyers abroad, some data necessarily travels
          across borders — for example, order and shipment details shared with an overseas buyer, or
          payment details exchanged with a foreign bank. Where this happens, we rely on the safeguards
          required under applicable law.
        </LegalBody>
      </LegalSection>

      <LegalSection>
        <LegalHeading>6. Data retention</LegalHeading>
        <LegalBody>
          We keep trade, customs, and payment records for as long as required by Indian export,
          tax, and financial recordkeeping rules, and delete or anonymize other account data when it is
          no longer needed for the purposes above.
        </LegalBody>
      </LegalSection>

      <LegalSection>
        <LegalHeading>7. Your rights</LegalHeading>
        <LegalBody>
          You can request access to, correction of, or deletion of your personal data, subject to our
          obligation to retain trade and compliance records where the law requires it. You can reach our
          Grievance Officer through the Help Center to make a request.
        </LegalBody>
      </LegalSection>

      <LegalSection>
        <LegalHeading>8. Security</LegalHeading>
        <LegalBody>
          We use encryption in transit, access controls, and monitoring to protect the data described
          above. No system is completely secure, and we will notify affected users if a breach
          materially affects them, as required by law.
        </LegalBody>
      </LegalSection>

      <LegalSection>
        <LegalHeading>9. Changes to this policy</LegalHeading>
        <LegalBody>
          If we make material changes to this policy, we will notify you through the platform before
          they take effect.
        </LegalBody>
      </LegalSection>

      <LegalSection>
        <LegalHeading>10. Contact</LegalHeading>
        <LegalBody>
          Questions about this policy or your data can be sent through the Help Center.
        </LegalBody>
      </LegalSection>
    </LegalPageLayout>
  );
};

export default PrivacyPolicyPage;
